/** Uniform index in [0, maxExclusive) using crypto.getRandomValues. */
export function secureRandomInt(maxExclusive) {
  if (maxExclusive <= 0) return 0;
  const limit = 0x100000000 - (0x100000000 % maxExclusive);
  const buf = new Uint32Array(1);
  let x;
  do {
    crypto.getRandomValues(buf);
    x = buf[0];
  } while (x >= limit);
  return x % maxExclusive;
}

export function pickRandom(arr) {
  return arr[secureRandomInt(arr.length)];
}

function shuffleChars(chars) {
  const a = [...chars];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = secureRandomInt(i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const BASE_LOWER = 'abcdefghijklmnopqrstuvwxyz';
const BASE_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const BASE_NUM = '0123456789';
const BASE_SYM = '!@#$%^&*()-_=+[]{};:,.?';

function filterAmbiguous(str, kind) {
  if (kind === 'lower') return str.replace(/[lo]/g, '');
  if (kind === 'upper') return str.replace(/[IO]/g, '');
  if (kind === 'num') return str.replace(/[01]/g, '');
  if (kind === 'sym') return str.replace(/[|"'`]/g, '');
  return str;
}

export function buildPools({ upper, lower, numbers, symbols, avoidAmbiguous }) {
  let l = BASE_LOWER;
  let u = BASE_UPPER;
  let n = BASE_NUM;
  let s = BASE_SYM;
  if (avoidAmbiguous) {
    l = filterAmbiguous(l, 'lower');
    u = filterAmbiguous(u, 'upper');
    n = filterAmbiguous(n, 'num');
    s = filterAmbiguous(s, 'sym');
  }
  const pools = [];
  if (lower && l.length) pools.push({ id: 'l', chars: l });
  if (upper && u.length) pools.push({ id: 'u', chars: u });
  if (numbers && n.length) pools.push({ id: 'n', chars: n });
  if (symbols && s.length) pools.push({ id: 's', chars: s });
  return pools;
}

export function charsetSizeFromPools(pools) {
  return pools.reduce((sum, p) => sum + p.chars.length, 0);
}

/** Ensures at least one char from each enabled pool; fills to `length`, then shuffles. */
export function generateRandomPassword(length, options) {
  const pools = buildPools(options);
  if (!pools.length) return '';

  const k = pools.length;
  const len = Math.max(length, k);
  const picks = pools.map((p) => pickRandom(p.chars.split('')));
  const combined = pools.map((p) => p.chars.split('')).flat();
  const out = [...picks];
  while (out.length < len) {
    out.push(pickRandom(combined));
  }
  return shuffleChars(out).join('');
}

export function generatePassphrase(wordCount, wordList) {
  const n = Math.max(3, Math.min(8, wordCount));
  const words = [];
  for (let i = 0; i < n; i += 1) {
    words.push(wordList[secureRandomInt(wordList.length)]);
  }
  const seps = ['-', '.', ' '];
  let s = words[0];
  for (let i = 1; i < words.length; i += 1) {
    s += pickRandom(seps) + words[i];
  }
  if (secureRandomInt(2) === 0) {
    s += pickRandom(seps) + String(secureRandomInt(90) + 10);
  }
  return s;
}

const LABELS = ['Weak', 'Fair', 'Strong', 'Very strong'];
const HINTS = [
  'Short or predictable — best for throwaway accounts only.',
  'Reasonable for everyday logins — a bit longer helps.',
  'Hard to guess — solid for most accounts.',
  'Excellent — suitable for banking, email, and password managers.',
];

/** @returns {{ bits: number, level: number, label: string, hint: string, fill: number }} */
export function estimateStrength({ memorable, password, pools, wordListLength, wordCount }) {
  let bits = 0;
  if (memorable && wordListLength > 1 && wordCount > 0) {
    bits = wordCount * Math.log2(wordListLength);
    if (/\d/.test(password)) bits += 3;
  } else {
    const size = charsetSizeFromPools(pools);
    if (size <= 1 || !password.length) {
      return { bits: 0, level: 0, label: LABELS[0], hint: HINTS[0], fill: 8 };
    }
    bits = password.length * Math.log2(size);
  }

  let level = 0;
  if (bits >= 72) level = 3;
  else if (bits >= 52) level = 2;
  else if (bits >= 36) level = 1;

  const fill = Math.min(100, Math.max(12, (bits / 90) * 100));

  return {
    bits,
    level,
    label: LABELS[level],
    hint: HINTS[level],
    fill,
  };
}

export function countEnabledCharOptions(o) {
  let n = 0;
  if (o.lower) n += 1;
  if (o.upper) n += 1;
  if (o.numbers) n += 1;
  if (o.symbols) n += 1;
  return n;
}
